import { createStoreContextItem } from '../Shared/StoreContextItem.tsx';
import { DisposableBase, type IInitializable } from '../Types/index.ts';
import { MediaDeviceStore } from './MediaDeviceStore.tsx';
import { FaceServiceStore, type CaptureSubmissionHandler } from './FaceServiceStore.ts';
import { FaceSdkStore, FaceSdkStoreContextItem } from './FaceSdkStore.tsx';
import { IdentitiesEventsStore } from './IdentitiesEventsStore.ts';
import { WebsocketStore } from '../SocketServices/WebsocketStore.tsx';
import { SettingsStore } from '../Setup/index.ts';
import { EventThemesStore } from './EventThemesStore.ts';
import { GuardModeStore } from './GuardModeStore.ts';
import { observable, reaction } from 'mobx';

export class MainStore extends DisposableBase implements IInitializable {
    readonly mediaDeviceStore = new MediaDeviceStore();
    readonly settingsStore = new SettingsStore();
    readonly websocketStore;
    readonly faceServiceStore;
    readonly identitiesEventsStore;
    readonly eventThemesStore = new EventThemesStore();
    readonly guardModeStore!: GuardModeStore;
    private _kioskHandler!: CaptureSubmissionHandler;

    // Authentication properties
    isAuthenticated = observable.box(false);
    showAuthDialog = observable.box(false);

    constructor(faceSdkStore: FaceSdkStore) {
        super();

        this.websocketStore = new WebsocketStore(this.settingsStore);
        this.faceServiceStore = new FaceServiceStore(faceSdkStore, this);
        this.identitiesEventsStore = new IdentitiesEventsStore(this.websocketStore);
        this.guardModeStore = new GuardModeStore(this.settingsStore, this.identitiesEventsStore, this.eventThemesStore, faceSdkStore);
        // Capture the default (kiosk) submission handler so we can restore it when not in guard mode.
        this._kioskHandler = this.faceServiceStore.submissionHandler;
    }

    private _applyAppMode = () => {
        this.faceServiceStore.submissionHandler =
            this.settingsStore.appMode === 'guard' ? this.guardModeStore.handleGuardCapture : this._kioskHandler;
    };

    async initialize() {
        // Settings must initialize first so SettingsStore.preferredCameraLabel
        // (loaded from localStorage and URL config) is available when
        // MediaDeviceStore enumerates and auto-selects a camera.
        await this.settingsStore.initialize(this.eventThemesStore);
        // Apply the persisted app mode now (kiosk vs guard) and re-apply on change.
        this._applyAppMode();
        this._disposers.push(reaction(() => this.settingsStore.appMode, this._applyAppMode));
        // Deterministically pause face detection the instant a guard result is shown
        // (the overlay's own effect fires a frame later, leaving a race window where a
        // new scan could start). Resume is handled when the overlay finishes dismissing.
        this._disposers.push(
            reaction(
                () => this.guardModeStore.resultVisible,
                (visible) => { if (visible) this.faceServiceStore.setEventOverlayActive(true); },
            ),
        );
        await this.mediaDeviceStore.initialize(this.settingsStore);
        await this.websocketStore.initialize();
        await this.identitiesEventsStore.initialize();
        // EventThemesStore doesn't need async initialization but persistence is set up in constructor
    }

    dispose() {
        super.dispose();
        this.faceServiceStore.dispose();
        this.guardModeStore.dispose();
        this.mediaDeviceStore.dispose();
        this.identitiesEventsStore.dispose();
        this.websocketStore.dispose();
        this.settingsStore.dispose();
        this.eventThemesStore.dispose();
    }

    // Authentication methods
    authenticate(pin: string): boolean {
        if (pin === this.settingsStore.authPin) {
            this.isAuthenticated.set(true);
            this.showAuthDialog.set(false);
            return true;
        }
        return false;
    }

    deAuthenticate(): void {
        this.isAuthenticated.set(false);
    }

    setShowAuthDialog(show: boolean): void {
        this.showAuthDialog.set(show);
    }

    requestAuthentication(): void {
        if (!this.isAuthenticated.get()) {
            this.showAuthDialog.set(true);
        }
    }
}

export const MainStoreContextItem = createStoreContextItem<MainStore>(() => {
    console.log('MainStoreContextItem: creating MainStore with FaceSdkStore', 'faceSdkStore');

    const faceSdkStore = FaceSdkStoreContextItem.useStore();

    return () => new MainStore(faceSdkStore);
});
